'use client';
import React, { useState } from 'react';
import { Gavel, FileText, Lock, ShieldAlert, Plus, ChevronDown, ChevronUp } from 'lucide-react';

interface EnforcementAction {
  id: string;
  parcelId: string;
  address: string;
  county: string;
  owner: string;
  actionType: 'notice' | 'lien' | 'seizure' | 'court';
  actionDate: string;
  issuedBy: string;
  status: 'active' | 'resolved' | 'escalated' | 'pending';
  description: string;
  linkedActions?: string[];
  amount?: number;
}

const ENFORCEMENT: EnforcementAction[] = [
  {
    id: 'ENF-2024-0031', parcelId: 'LR-SIN-0007', address: 'Greenville Port Area', county: 'Sinoe', owner: 'Unknown',
    actionType: 'seizure', actionDate: '2024-04-10', issuedBy: 'Commissioner J. Jallah',
    status: 'active', description: 'Seizure order issued after 210 days delinquency. Property access restricted pending tax settlement or court order.',
    linkedActions: ['ENF-2024-0018', 'ENF-2024-0022'], amount: 9300,
  },
  {
    id: 'ENF-2024-0028', parcelId: 'LR-LOF-0019', address: 'Voinjama Riverside', county: 'Lofa', owner: 'Contested — 2 claimants',
    actionType: 'court', actionDate: '2024-03-25', issuedBy: 'LRA Legal Division',
    status: 'escalated', description: 'Case referred to Lofa County Court. Boundary dispute and tax delinquency combined case. Hearing scheduled May 2024.',
    linkedActions: ['ENF-2024-0015'], amount: 2184,
  },
  {
    id: 'ENF-2024-0025', parcelId: 'LR-NIM-0008', address: 'Ganta Market Zone B', county: 'Nimba', owner: 'Unknown',
    actionType: 'lien', actionDate: '2024-03-10', issuedBy: 'Revenue Officer M. Pewee',
    status: 'active', description: 'Tax lien filed with Nimba County Registry. Property cannot be transferred or mortgaged until tax debt cleared.',
    amount: 3840,
  },
  {
    id: 'ENF-2024-0022', parcelId: 'LR-MAR-0022', address: 'Kakata Industrial Blvd', county: 'Margibi', owner: 'Unknown',
    actionType: 'notice', actionDate: '2024-04-20', issuedBy: 'Revenue Officer T. Flomo',
    status: 'pending', description: 'First formal notice issued. Owner has 30 days to respond or pay outstanding balance before lien filing.',
    amount: 5280,
  },
  {
    id: 'ENF-2024-0019', parcelId: 'LR-GBS-0055', address: 'Buchanan Port Zone', county: 'Grand Bassa', owner: 'LPA Holdings',
    actionType: 'lien', actionDate: '2024-03-28', issuedBy: 'Revenue Officer B. Kpehe',
    status: 'resolved', description: 'Lien filed for partial non-payment. Partial payment of $12,000 received. Lien remains until full balance cleared.',
    amount: 14040,
  },
];

const actionTypeConfig = {
  notice: { color: '#60a5fa', bg: 'rgba(96,165,250,0.1)', label: 'Notice Issued', icon: FileText },
  lien: { color: '#fbbf24', bg: 'rgba(251,191,36,0.1)', label: 'Lien Placed', icon: Lock },
  seizure: { color: '#f87171', bg: 'rgba(248,113,113,0.1)', label: 'Seizure Order', icon: ShieldAlert },
  court: { color: '#a78bfa', bg: 'rgba(167,139,250,0.1)', label: 'Court Referral', icon: Gavel },
};

const statusConfig = {
  active: { color: '#f87171', label: 'Active' },
  resolved: { color: '#34d399', label: 'Resolved' },
  escalated: { color: '#a78bfa', label: 'Escalated' },
  pending: { color: '#fbbf24', label: 'Pending' },
};

export default function EnforcementActionPanel() {
  const [expandedId, setExpandedId] = useState<string | null>('ENF-2024-0031');
  const [filterType, setFilterType] = useState<string>('all');

  const filtered = ENFORCEMENT.filter((e) => filterType === 'all' || e.actionType === filterType);

  const formatCurrency = (val: number) => `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {Object.entries(actionTypeConfig).map(([type, cfg]) => {
          const TypeIcon = cfg.icon;
          const count = ENFORCEMENT.filter((e) => e.actionType === type).length;
          return (
            <div key={type} className="rounded-xl p-3" style={{ backgroundColor: 'var(--dark-card)', border: `1px solid ${cfg.color}30` }}>
              <div className="flex items-center gap-2 mb-1">
                <TypeIcon size={14} style={{ color: cfg.color }} />
                <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{cfg.label}</span>
              </div>
              <p className="text-xl font-bold font-mono-data" style={{ color: cfg.color }}>{count}</p>
            </div>
          );
        })}
      </div>

      {/* Filter + New */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          {['all', 'notice', 'lien', 'seizure', 'court'].map((f) => (
            <button
              key={f}
              onClick={() => setFilterType(f)}
              className="px-2.5 py-1 rounded-full text-xs font-medium transition-all capitalize"
              style={{
                backgroundColor: filterType === f ? 'rgba(212,160,23,0.2)' : 'var(--dark-elevated)',
                color: filterType === f ? 'var(--accent)' : 'var(--dark-muted)',
                border: `1px solid ${filterType === f ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`,
              }}
            >
              {f === 'all' ? 'All Actions' : f}
            </button>
          ))}
        </div>
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-90"
          style={{ background: 'linear-gradient(135deg, #b8860b 0%, #d4a017 100%)', color: '#070e1f' }}>
          <Plus size={12} />
          Log Action
        </button>
      </div>

      {/* Enforcement log */}
      <div className="space-y-3">
        {filtered.map((action) => {
          const cfg = actionTypeConfig[action.actionType];
          const ActionIcon = cfg.icon;
          const statusCfg = statusConfig[action.status];
          const isExpanded = expandedId === action.id;

          return (
            <div key={action.id} className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--dark-card)', border: `1px solid ${cfg.color}25` }}>
              <button
                className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white/2 transition-colors"
                onClick={() => setExpandedId(isExpanded ? null : action.id)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: cfg.bg }}>
                    <ActionIcon size={16} style={{ color: cfg.color }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold font-mono-data" style={{ color: 'var(--accent)' }}>{action.id}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: statusCfg.color + '15', color: statusCfg.color }}>{statusCfg.label}</span>
                    </div>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>
                      {action.parcelId} · {action.address} · {action.county}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {action.amount && (
                    <span className="text-xs font-bold font-mono-data hidden sm:block" style={{ color: '#f87171' }}>
                      {formatCurrency(action.amount)}
                    </span>
                  )}
                  {isExpanded ? <ChevronUp size={16} style={{ color: 'var(--dark-muted)' }} /> : <ChevronDown size={16} style={{ color: 'var(--dark-muted)' }} />}
                </div>
              </button>

              {isExpanded && (
                <div className="px-4 pb-4 border-t" style={{ borderColor: 'var(--dark-border)' }}>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
                    <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
                      <p className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-text)' }}>Action Details</p>
                      <div className="space-y-1.5">
                        {[
                          { label: 'Action Date', value: action.actionDate },
                          { label: 'Issued By', value: action.issuedBy },
                          { label: 'Owner', value: action.owner },
                          ...(action.amount ? [{ label: 'Amount Involved', value: formatCurrency(action.amount) }] : []),
                        ].map((row) => (
                          <div key={row.label} className="flex justify-between text-xs">
                            <span style={{ color: 'var(--dark-muted)' }}>{row.label}</span>
                            <span style={{ color: 'var(--dark-text)' }}>{row.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="p-3 rounded-lg" style={{ backgroundColor: cfg.bg, border: `1px solid ${cfg.color}30` }}>
                      <p className="text-xs font-semibold mb-1.5 flex items-center gap-1.5" style={{ color: cfg.color }}>
                        <ActionIcon size={11} />
                        Action Description
                      </p>
                      <p className="text-xs leading-relaxed" style={{ color: 'var(--dark-muted)' }}>{action.description}</p>
                      {action.linkedActions && (
                        <div className="mt-2">
                          <p className="text-xs font-semibold mb-1" style={{ color: 'var(--dark-muted)' }}>Linked Actions:</p>
                          <div className="flex flex-wrap gap-1">
                            {action.linkedActions.map((la) => (
                              <span key={la} className="text-xs px-1.5 py-0.5 rounded font-mono-data"
                                style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)' }}>
                                {la}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
